// Added By Farhan Khan on 24-Dec-2025
import React, { useState, useEffect, useMemo } from "react";
import { Badge, Button, Col, Container, Row, Spinner, Table, Tab, Tabs } from "react-bootstrap"; // Added Tabs, Tab for BOM Line Items tab
import Confirm from "../Confirm";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import solisPowerApi from "../../api/solisPowerApi";
import Helmet from "react-helmet";
import Main from "../layout/Main";
import { formatDateForView, getUserDetails } from "../../utils/helper";
import PERMISSIONS from "../../constants/permissions";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import PubSub from "pubsub-js";
import InfoPill from "../InfoPill";
import { generateQuotationPDF } from "../../utils/generateQuotationPDF"; // Added By Farhan Khan on 24-Dec-2025 - PDF Generation (Updated: 4-page structure)
import QuotationLineItems from "./QuotationLineItems";

const QuotationView = (props) => {
  const userInfo = getUserDetails();
  const location = useLocation();
  const navigate = useNavigate();
  const [quotation, setQuotation] = useState(location.state ? location.state : {});
  const [items, setItems] = useState([]);
  const [quotationBomItems, setQuotationBomItems] = useState([]); // Added By Farhan Khan on 24-Dec-2025
  const [bomItems, setBomItems] = useState([]); // BOM Line Items – View Only (Quotation Detail)
  const [mountingStructureItems, setMountingStructureItems] = useState([]); // Created on 2026-02-07 by Farhan Khan - Mounting Structure items
  const [activeTab, setActiveTab] = useState("line-items"); // BOM Line Items – View Only (Quotation Detail)
  const [modalShow, setModalShow] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false); // BUG FIX: PDF generation loading state - Added By Farhan Khan on 24-Dec-2025
  const [makeOptions, setMakeOptions] = useState([]); // Master Brands for ID -> Label mapping

  useEffect(() => {
    fetchQuotation();
    fetchActiveBrands();
  }, []);

  const fetchActiveBrands = async () => {
    try {
      const res = await solisPowerApi.fetchActiveBrands();
      if (res) {
        setMakeOptions(res.map(b => ({ value: b.id, label: b.name })));
      }
    } catch (err) {
      console.error("[QuotationView] Error fetching brands:", err);
    }
  };

  // Create a lookup map for UUID -> Label
  const makeMap = useMemo(() => {
    const map = {};
    (makeOptions || []).forEach(opt => {
      map[opt.value] = opt.label;
    });
    return map;
  }, [makeOptions]);

  // BOM Line Items – View Only (Quotation Detail)
  // Fetch BOM items when quotation ID is available
  useEffect(() => {
    if (quotation && quotation.id) {
      fetchBomItems(quotation.id);
    }
  }, [quotation?.id]);

  // Created on 2026-02-07 by Farhan Khan - Fetch mounting structure items from new API
  // Fixed on 2026-02-07 by Farhan Khan - Use new quotation_mounting_structures API
  useEffect(() => {
    const fetchMountingStructures = async () => {
      if (quotation && quotation.id) {
        try {
          const mountingStructuresData = await solisPowerApi.fetchQuotationMountingStructures(quotation.id);

          console.log('[QuotationView] Fetched mounting structures:', mountingStructuresData);

          // ✅ FIX HERE
          const apiData = mountingStructuresData?.mounting_structures || [];

          if (Array.isArray(apiData) && apiData.length > 0) {

            const mappedMountingItems = apiData.map((ms, index) => {
              const partsWithArrays = (ms.parts || []).map(part => ({
                ...part,
                special_structure_type: part.special_structure_type || ms.special_structure_type || "",
                special_structure_specs: part.special_structure_specs || ms.special_structure_specs || part.specification || "",
                special_structure_length: part.special_structure_length ?? ms.special_structure_length ?? "",
                shape: part.shape
                  ? (typeof part.shape === 'string'
                    ? part.shape.split(',').filter(s => s.trim())
                    : Array.isArray(part.shape) ? part.shape : [])
                  : [],
                make: part.make
                  ? (typeof part.make === 'string'
                    ? part.make.split(',').filter(m => m.trim())
                    : Array.isArray(part.make) ? part.make : [])
                  : [],
              }));

              console.log("SPECIAL DEBUG [View]:", {
                length: ms.special_structure_length,
                type: ms.special_structure_type
              });

              return {
                ...ms,
                id: ms.id || `temp_${Date.now()}_${index}`,
                special_structure_length:
                  ms.special_structure_length !== null &&
                    ms.special_structure_length !== undefined
                    ? ms.special_structure_length
                    : "",
                special_structure_type:
                  ms.special_structure_type !== null &&
                    ms.special_structure_type !== undefined
                    ? ms.special_structure_type
                    : "",
                special_structure_specs: ms.special_structure_specs || "",
                parts: partsWithArrays,
              };
            });

            console.log("MappedMountingItems Final", mappedMountingItems);

            setMountingStructureItems(mappedMountingItems);
          } else {
            setMountingStructureItems([]);
          }

        } catch (error) {
          console.error('[QuotationView] Error fetching mounting structures:', error);
          setMountingStructureItems([]);
        }
      } else {
        setMountingStructureItems([]);
      }
    };

    fetchMountingStructures();
  }, [quotation?.id]);

  // BOM Line Items – View Only (Quotation Detail)
  // Fetch BOM items for the quotation
  const fetchBomItems = async (quotationId) => {
    try {
      const bomItemsData = await solisPowerApi.fetchQuotationBomItemsByQuotationId(quotationId);
      if (bomItemsData && Array.isArray(bomItemsData)) {
        // Created on 2026-02-07 by Farhan Khan - Filter out mounting structure items from BOM items
        // Mounting structure items have display_category containing "Mounting"
        const standardBomItems = bomItemsData.filter(item =>
          !item.display_category || !item.display_category.toLowerCase().includes('mounting')
        );

        // Sort by sort_order ASC, nulls last
        const sortedBomItems = standardBomItems.sort((a, b) => {
          const orderA = a.sort_order !== null && a.sort_order !== undefined ? a.sort_order : 999999;
          const orderB = b.sort_order !== null && b.sort_order !== undefined ? b.sort_order : 999999;
          return orderA - orderB;
        });
        setBomItems(sortedBomItems);
      } else {
        setBomItems([]);
      }
    } catch (error) {
      console.error("Error fetching BOM items:", error);
      setBomItems([]);
    }
  };

  const fetchQuotation = () => {
    let qId = quotation?.id;
    if (!qId && location.hasOwnProperty("pathname") && location.pathname.split("/").length >= 3) {
      qId = location.pathname.split("/")[2];
    }

    async function initQuotation() {
      try {
        setLoading(true);
        // Fixed on 2026-02-07 by Farhan Khan - Fetch with items to get lead and created_by_user data
        // fetchQuotationById already uses with_items=true by default
        let result = await solisPowerApi.fetchQuotationById(qId);

        if (result) {
          console.log('[QuotationView] Fetched quotation data:', result);
          console.log('[QuotationView] Lead data:', result.lead);
          console.log('[QuotationView] Created by user:', result.created_by_user);

          setQuotation(result);
          setItems(result.items || []);
          // Added By Farhan Khan on 24-Dec-2025
          setQuotationBomItems(result.quotation_bom_items || []);
        } else {
          setQuotation({});
          setItems([]);
          setQuotationBomItems([]);
        }
      } catch (error) {
        console.error("Error fetching quotation:", error);
        setQuotation({});
        setItems([]);
        setQuotationBomItems([]);
      } finally {
        setLoading(false);
      }
    }

    if (qId) {
      initQuotation();
    } else {
      setLoading(false);
    }
  };

  const handleMarkAsSent = async () => {
    try {
      if (!quotation?.id) return;

      const updateData = { ...quotation, status: "Sent" };
      // Delete unnecessary calculated fields before save
      delete updateData.lead;
      delete updateData.created_by_user;
      delete updateData.owner_ids; // Model handles it

      const result = await solisPowerApi.saveQuotation(updateData);

      if (result && result.success) {
        PubSub.publish("RECORD_SAVED_TOAST", {
          title: "Status Updated",
          message: "Quotation marked as Sent",
        });

        // Auto-update Project Status to "Quotation Shared"
        if (quotation.lead_id) {
          try {
            await solisPowerApi.updateProjectStatusesByLeadId(quotation.lead_id, "Quotation Shared");
          } catch (err) {
            console.warn("[QuotationView] Auto-status update failed:", err);
          }
        }

        fetchQuotation();
      } else {
        PubSub.publish("RECORD_ERROR_TOAST", {
          title: "Error",
          message: result?.errors || "Failed to update status",
        });
      }
    } catch (error) {
      console.error("Error marking quotation as sent:", error);
    }
  };

  const deleteQuotation = async () => {
    const result = await solisPowerApi.deleteQuotation(location?.state.id ? location?.state.id : quotation?.id);
    if (result) {
      PubSub.publish("RECORD_SAVED_TOAST", {
        title: "Deleted ",
        message: "Record Deleted successfully",
      });
      navigate(`/quotations`);
    }
  };

  const editQuotation = () => {
    navigate(`/quotations/${quotation.id}/e`, { state: quotation });
  };

  // PDF Generation – Download Quotation PDF
  // Added By Farhan Khan on 24-Dec-2025
  // BUG FIX: Added loading state, better error handling, defensive checks
  const handleDownloadPDF = async () => {
    // Defensive check: Ensure quotation exists
    if (!quotation || !quotation.id) {
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: "Quotation data not available. Please refresh the page.",
      });
      return;
    }

    // Prevent multiple simultaneous PDF generations
    if (isGeneratingPDF) {
      return;
    }

    setIsGeneratingPDF(true);

    try {
      console.log("PDF Generation - Starting for quotation:", quotation.id);

      PubSub.publish("RECORD_SAVED_TOAST", {
        title: "Generating PDF",
        message: "Please wait while we generate your quotation PDF...",
      });

      // Fetch quotation data
      const quotationData = await solisPowerApi.fetchQuotationDataForPDF(quotation.id);

      console.log("PDF Generation - Raw API Response:", quotationData);
      console.log("PDF Generation - Data received:", {
        hasQuotation: !!quotationData?.quotation,
        hasLineItems: Array.isArray(quotationData?.lineItems),
        hasBomItems: Array.isArray(quotationData?.bomItems),
        lineItemsCount: quotationData?.lineItems?.length || 0,
        bomItemsCount: quotationData?.bomItems?.length || 0,
        quotationKeys: quotationData?.quotation ? Object.keys(quotationData.quotation) : [],
        firstLineItem: quotationData?.lineItems?.[0],
        firstBomItem: quotationData?.bomItems?.[0]
      });

      if (!quotationData) {
        throw new Error("Failed to fetch quotation data from server");
      }

      if (!quotationData.quotation) {
        throw new Error("Quotation data is missing or invalid");
      }

      // BUG FIX: Data normalization - Handle different data shapes
      // Added By Farhan Khan on 24-Dec-2025
      // Ensure data structure matches PDF expectations
      const normalizedQuotationData = {
        quotation: {
          ...quotationData.quotation,
          // Ensure CREATEDBYNAME is available (check multiple possible field names)
          CREATEDBYNAME: quotationData.quotation.CREATEDBYNAME ||
            quotationData.quotation.createdbyname ||
            quotationData.quotation.created_by_name ||
            quotationData.quotation.CREATEDBYNAME ||
            'N/A',
          bom_sections: quotationData.quotation.bom_sections || quotationData.quotation.bom || quotation.bom_sections || quotation.bom || [],
          mounting_structure_sections: quotationData.quotation.mounting_structure_sections || quotationData.quotation.mounting_structure || quotation.mounting_structure_sections || quotation.mounting_structure || []
        },
        // Normalize line items - handle different field names
        lineItems: quotationData.lineItems ||
          quotationData.quotation_line_items ||
          quotationData.items ||
          [],
        // Normalize BOM items - handle different field names
        bomItems: quotationData.bomItems ||
          quotationData.quotation_bom_items ||
          quotationData.bom_items ||
          []
      };

      // BUG FIX: Fetch Lead data for Bill To section
      // Added By Farhan Khan on 24-Dec-2025
      // quotation.lead_id is the customer ID - fetch lead details for Bill To
      let leadData = null;
      if (normalizedQuotationData.quotation.lead_id) {
        try {
          console.log("PDF Generation - Fetching lead data for lead_id:", normalizedQuotationData.quotation.lead_id);
          leadData = await solisPowerApi.fetchLeadById(normalizedQuotationData.quotation.lead_id);
          console.log("PDF Generation - Lead data fetched:", leadData);
        } catch (error) {
          console.warn("PDF Generation - Failed to fetch lead data:", error);
          // Continue without lead data - use quotation customer fields as fallback
        }
      }

      console.log("PDF Generation - Normalized Data:", {
        quotationId: normalizedQuotationData.quotation.id,
        quotationEstimateNumber: normalizedQuotationData.quotation.estimate_number,
        createdByName: normalizedQuotationData.quotation.CREATEDBYNAME,
        hasLeadData: !!leadData,
        lineItemsCount: normalizedQuotationData.lineItems.length,
        bomItemsCount: normalizedQuotationData.bomItems.length,
        firstLineItem: normalizedQuotationData.lineItems[0],
        firstBomItem: normalizedQuotationData.bomItems[0]
      });

      // ✅ STEP 1 & 2: USE UI SOURCE OF TRUTH (REUSE STATE)
      // Added By Farhan Khan on 24-Dec-2025 - Refactored to skip redundant re-fetching
      const currentMountingStructure = mountingStructureItems || [];

      // ✅ STEP 4: BLOCK PDF UNTIL DATA IS READY (MOST IMPORTANT)
      if (!currentMountingStructure || currentMountingStructure.length === 0) {
        console.warn("Mounting Structure empty before PDF (reusing UI state)");
      }

      console.log("UI DATA (MOUNTING)", currentMountingStructure);

      console.log("QUOTATION DATA FOR PDF", {
        ...normalizedQuotationData,
        leadData: leadData,
        mountingStructure: currentMountingStructure
      });

      // Generate PDF with normalized data, lead data, and mounting structures
      await generateQuotationPDF({
        ...normalizedQuotationData,
        leadData: leadData,
        mountingStructure: currentMountingStructure
      });

      PubSub.publish("RECORD_SAVED_TOAST", {
        title: "PDF Generated",
        message: "Quotation PDF downloaded successfully",
      });

      // Updated By System on 2026-04-29 - Event-Based Project Status Update
      // Mark as Shared when PDF is generated/downloaded
      if (normalizedQuotationData.quotation.lead_id) {
        try {
          await solisPowerApi.updateProjectStatusesByLeadId(normalizedQuotationData.quotation.lead_id, "Quotation Shared");
        } catch (err) {
          console.warn("[QuotationView] Auto-status update to Quotation Shared failed:", err);
        }
      }
    } catch (error) {
      console.error("PDF Generation - Error:", error);
      console.error("PDF Generation - Error Stack:", error.stack);

      // Show user-friendly error message
      const errorMessage = error?.message || "Failed to generate quotation PDF. Please try again.";
      PubSub.publish("RECORD_ERROR_TOAST", {
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Table headers for Quotation Line Items
  const header = [];
  header.push(
    {
      title: "Product",
      prop: "product_name",
      isFilterable: true,
      // isSortable: true,
    },
    {
      title: "Description",
      prop: "product_description",
      isFilterable: true,
      // isSortable: true,
    },
    {
      title: "Brand",
      prop: "brand_name",
      isFilterable: true,
      // isSortable: true,
    },
    {
      title: "Quantity",
      prop: "quantity",
      // isSortable: true,
      // isFilterable: true,
    },
    {
      title: "Unit",
      prop: "unit",
      isFilterable: true,
      // isSortable: true,
    },
    {
      title: "Unit Price",
      prop: "unit_price",
      // isSortable: true,
      // isFilterable: true,
      cell: (row) => (
        row.unit_price !== null && row.unit_price !== undefined
          ? parseFloat(row.unit_price || 0).toFixed(2)
          : "-"
      ),
    },
    {
      title: "Discount",
      prop: "discount",
      // isSortable: true,
      // isFilterable: true,
      cell: (row) => (
        row.discount !== null && row.discount !== undefined
          ? parseFloat(row.discount || 0).toFixed(2)
          : "-"
      ),
    },
    {
      title: "Net Amount",
      prop: "net_amount",
      // isSortable: true,
      isFilterable: true,
      cell: (row) => (
        row.net_amount !== null && row.net_amount !== undefined
          ? <span className="fw-bold">{parseFloat(row.net_amount || 0).toFixed(2)}</span>
          : "-"
      ),
    }
  );

  const labels = {
    beforeSelect: " ",
  };

  if (loading) {
    return (
      <Main>
        <Container>
          <Row>
            <Col className="text-center mt-5">
              <Spinner animation="border" />
            </Col>
          </Row>
        </Container>
      </Main>
    );
  }

  if (!quotation || !quotation.id) {
    return (
      <Main>
        <Container>
          <Row className="view-form">
            <Col lg={11} className="pb-3 pt-2">
              <Link className="nav-link" to="/quotations">
                Home <i className="fa-solid fa-chevron-right"></i>{" "}
                <div style={{ color: "#23468c", display: "inline" }}>
                  Quotation
                </div>
              </Link>
            </Col>
            <Col></Col>
          </Row>
          <Row className="view-form gx-5 mx-auto">
            <Col lg={9}>
              <Row className="view-form-header bg-custom align-items-center">
                <Col lg={12} className="text-center py-4">
                  <h5>Record Not Found</h5>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </Main>
    );
  }

  return (
    <Main>
      <Container>
        {quotation && (
          <>
            {modalShow && (
              <Confirm
                show={modalShow}
                onHide={() => setModalShow(false)}
                deleteProduct={deleteQuotation}
                title="Confirm delete?"
                message="You are going to delete the record. Are you sure?"
                table="Quotation"
              />
            )}
            <Row className="view-form">
              <Col lg={11} className="pb-3 pt-2">
                <Link className="nav-link" to="/quotations">
                  Home <i className="fa-solid fa-chevron-right"></i>{" "}
                  <div style={{ color: "#23468c", display: "inline" }}>
                    Quotation
                  </div>
                </Link>
              </Col>
              <Col></Col>
            </Row>
            {/* Updated Layout - Added By Farhan Khan on 24-Dec-2025 */}
            {/* Match reference layout: Page Title, Header (3 columns), Line Items Table, Total Summary, Footer */}
            <Row className="view-form gx-5 mx-auto">
              <Col lg={12}>
                {/* Action Buttons Row */}
                <Row className="mb-3">
                  <Col lg={12} className="d-flex justify-content-end">
                    {/* PDF Generation – Download Button */}
                    {/* Added By Farhan Khan on 24-Dec-2025 */}
                    {/* BUG FIX: Added loading state and disabled state */}
                    {/* Mark as Sent Button - Added By System on 2026-04-29 */}
                    {quotation?.status !== "Sent" && quotation?.status !== "Approved" && quotation?.status !== "Rejected" && (
                      <Button
                        size="sm"
                        variant="info"
                        className="mx-2 text-white"
                        onClick={handleMarkAsSent}
                        disabled={isGeneratingPDF}
                      >
                        <i className="fa fa-paper-plane me-1"></i> Mark as Sent
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="btn-create mx-2"
                      onClick={handleDownloadPDF}
                      disabled={isGeneratingPDF || !quotation?.id}
                      title={isGeneratingPDF ? "Generating PDF..." : "Download Quotation as PDF"}
                    >
                      {isGeneratingPDF ? (
                        <>
                          <i className="fa fa-spinner fa-spin me-2"></i> Generating...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-download"></i> Download Quotation
                        </>
                      )}
                    </Button>
                    {[PERMISSIONS.MODIFY_ALL].some(permission => {
                      return userInfo?.permissions.map(p => p.name).includes(permission);
                    }) && (
                        <Button
                          size="sm"
                          className="btn-edit-quat mx-2"
                          onClick={() => editQuotation(true)}
                        >
                          <i className="fa-regular fa-pen-to-square"></i>
                        </Button>
                      )}
                    {[PERMISSIONS.DELETE_ALL].some(permission => {
                      return userInfo?.permissions.map(p => p.name).includes(permission);
                    }) && (
                        <Button
                          className="btn-sm"
                          variant="danger"
                          onClick={() => setModalShow(true)}
                        >
                          <i className="fa fa-trash-o"></i>
                        </Button>
                      )}
                  </Col>
                </Row>

                {/* Page Title - Center Aligned */}
                <Row className="mb-4">
                  <Col lg={12} className="text-center">
                    <h2 style={{
                      color: "#364794",
                      fontWeight: "700",
                      marginBottom: "0.5rem"
                    }}>
                      Quotation
                    </h2>
                  </Col>
                </Row>

                {/* Header Section - 3 Columns */}
                <Row className="mb-4" style={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "1.5rem",
                  border: "1px solid #e9ecef"
                }}>
                  {/* LEFT COLUMN - From */}
                  <Col lg={4} md={4} sm={12} className="mb-3 mb-md-0">
                    <h6 style={{
                      color: "#364794",
                      fontWeight: "600",
                      marginBottom: "0.75rem",
                      textTransform: "uppercase"
                    }}>
                      From
                    </h6>
                    <div style={{ fontSize: "0.95rem", lineHeight: "1.8" }}>
                      <div style={{ fontWeight: "600", marginBottom: "0.25rem" }}>Solis Power Greentech Pvt Ltd</div>
                      <div>SB-10, Narayan Vihar, Ajmer Road, Jaipur</div>
                      <div>Rajasthan, 302035</div>
                      <div>India</div>
                      <div style={{ marginTop: "0.5rem", fontWeight: "500" }}>GSTIN : 08ACYFS1632G1ZQ</div>
                    </div>
                  </Col>

                  {/* MIDDLE COLUMN - Bill To */}
                  {/* Fixed on 2026-02-07 by Farhan Khan - Bind from lead data */}
                  <Col lg={4} md={4} sm={12} className="mb-3 mb-md-0">
                    <h6 style={{
                      color: "#364794",
                      fontWeight: "600",
                      marginBottom: "0.75rem",
                      textTransform: "uppercase"
                    }}>
                      Bill To
                    </h6>
                    <div style={{ fontSize: "0.95rem", lineHeight: "1.8" }}>
                      <div style={{ fontWeight: "600", marginBottom: "0.25rem" }}>
                        {quotation?.lead?.name || quotation?.lead?.company_name || quotation?.customer_name || "N/A"}
                      </div>
                      {quotation?.lead?.address && (
                        <div>
                          {quotation.lead.address}
                        </div>
                      )}
                      {(!quotation?.lead?.address) && (
                        <div>
                          {quotation?.customer_state || ""}{quotation?.customer_state && quotation?.customer_city ? ", " : ""}
                          {quotation?.customer_city || ""}
                          {quotation?.customer_state || quotation?.customer_city ? ", India" : "India"}
                        </div>
                      )}
                      {quotation?.lead?.phone && (
                        <div style={{ marginTop: "0.25rem" }}>
                          Phone : {quotation.lead.phone}
                        </div>
                      )}
                      {(!quotation?.lead?.phone) && quotation?.customer_mobile && (
                        <div style={{ marginTop: "0.25rem" }}>
                          Mobile : {quotation.customer_mobile}
                        </div>
                      )}
                      {quotation?.lead?.email && (
                        <div style={{ marginTop: "0.25rem" }}>
                          Email : {quotation.lead.email}
                        </div>
                      )}
                      {quotation?.lead?.gst_no && (
                        <div style={{ marginTop: "0.25rem" }}>
                          GSTIN : {quotation.lead.gst_no}
                        </div>
                      )}
                    </div>
                  </Col>

                  {/* RIGHT COLUMN - Meta Info */}
                  <Col lg={4} md={4} sm={12}>
                    <h6 style={{
                      color: "#364794",
                      fontWeight: "600",
                      marginBottom: "0.75rem",
                      textTransform: "uppercase"
                    }}>
                      Meta Info
                    </h6>
                    <div style={{ fontSize: "0.95rem", lineHeight: "1.8" }}>
                      <div><strong>Date:</strong> {formatDateForView(quotation?.quotation_date || "") || "N/A"}</div>
                      <div><strong>Expiry Date:</strong> {formatDateForView(quotation?.expiry_date || "") || "N/A"}</div>
                      <div><strong>Estimate#:</strong> {quotation?.estimate_number || "N/A"}</div>
                      {/* Fixed on 2026-02-07 by Farhan Khan - Bind Created By from created_by_user */}
                      <div><strong>Created By:</strong> {quotation?.created_by_user?.name || quotation?.CREATEDBYNAME || "N/A"}</div>
                      {/* Standardized Assigned User - Added By System on 2026-04-29 */}
                      <div>
                        <strong>Assigned User:</strong> {(quotation?.assigned_user || quotation?.lead?.assigned_user)?.label || (quotation?.assigned_user || quotation?.lead?.assigned_user)?.name || "N/A"}
                      </div>
                      {/* Fixed on 2026-02-07 by Farhan Khan - Bind Phone from lead or customer_mobile */}
                      <div><strong>Contact:</strong> {quotation?.lead?.phone || quotation?.customer_mobile || "N/A"}</div>
                    </div>
                  </Col>
                </Row>

                {/* Unified Line Items Component (Resolves Issue 3 UI Consistency) */}
                <Row className="mb-4">
                  <Col lg={12}>
                    <div style={{
                      backgroundColor: "#fff",
                      borderRadius: "8px",
                      padding: "1.5rem",
                      border: "1px solid #e9ecef"
                    }}>
                      <QuotationLineItems
                        items={items}
                        quotation={quotation}
                        readOnly={true}
                        productOptions={[]}
                      />
                    </div>
                  </Col>
                </Row>

                {/* Footer Section */}
                <Row style={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "1.5rem",
                  border: "1px solid #e9ecef"
                }}>
                  {/* LEFT - Notes */}
                  <Col lg={4} md={4} sm={12} className="mb-3 mb-md-0">
                    <h6 style={{
                      color: "#364794",
                      fontWeight: "600",
                      marginBottom: "0.75rem"
                    }}>
                      Notes:
                    </h6>
                    <div style={{ fontSize: "0.9rem", lineHeight: "1.6" }}>
                      Please Refer Detailed Terms & Condition for payment & Warranty
                    </div>
                  </Col>

                  {/* MIDDLE - Bank Details */}
                  <Col lg={4} md={4} sm={12} className="mb-3 mb-md-0">
                    <h6 style={{
                      color: "#364794",
                      fontWeight: "600",
                      marginBottom: "0.75rem"
                    }}>
                      Bank Detail:
                    </h6>
                    <div style={{ fontSize: "0.9rem", lineHeight: "1.8" }}>
                      <div><strong>Bank Name:</strong> ICICI BANK LTD</div>
                      <div><strong>Account Name:</strong> SOLISPOWER GREENTECH PVT LTD</div>
                      <div><strong>Account No:</strong> 361305500447</div>
                      <div><strong>IFSC:</strong>  ICIC0003613</div>
                      <div><strong>Branch:</strong> BHANKROTA, JAIPUR</div>
                    </div>
                  </Col>

                  {/* RIGHT - Authorized Signature */}
                  <Col lg={4} md={4} sm={12}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{
                        borderTop: "2px solid #364794",
                        paddingTop: "3rem",
                        marginTop: "2rem",
                        minHeight: "100px"
                      }}>
                        <div style={{
                          fontWeight: "600",
                          color: "#364794",
                          marginBottom: "0.5rem"
                        }}>
                          Authorized Signature
                        </div>
                        {/* Fixed on 2026-02-07 by Farhan Khan - Bind Authorized Signature from created_by_user */}
                        <div style={{
                          fontSize: "0.9rem",
                          color: "#6c757d",
                          marginTop: "1rem"
                        }}>
                          {quotation?.created_by_user?.name || quotation?.CREATEDBYNAME || ""}
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>

            {/* BOM Line Items – View Only (Quotation Detail) */}
            {/* Tabs for Quotation Line Items and BOM Line Items */}
            <Row className="view-form gx-5 mx-auto mt-4">
              <Col lg={12}>
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="mb-3"
                >
                  {/* Tab 1: Quotation Line Items */}
                  <Tab eventKey="line-items" title="Quotation Line Items">
                    <Row className="view-form-header bg-custom align-items-center mt-3">
                      <Col lg={12}>
                        <h5>Quotation Line Items</h5>
                      </Col>
                    </Row>

                    <DatatableWrapper
                      body={items}
                      headers={header}
                      paginationOptionsProps={{
                        initialState: {
                          rowsPerPage: 50,
                          options: [5, 10, 15, 20, 30, 50]
                        },
                      }}
                    >
                      <Row className="align-items-end my-2">
                        <Col lg={2} md={4} xl={2} sm={12}>
                          <Filter
                            classes={{
                              inputGroup: "",
                              input: "",
                              clearButton: "btn-custom",
                            }}
                          />
                        </Col>
                        <Col md={4} xl={1} lg={1} sm={12}>
                          <PaginationOptions labels={labels} />
                        </Col>
                        {/* <Col lg={2} md={4} sm={12} className="py-1">
                          <div className="d-flex py-1 px-2 ">
                            <InfoPill
                              left="Total"
                              right={items?.length}
                              rightClasses="bg-custom text-white border-white fw-bold"
                              leftClasses="bg-white  fw-bold "
                            />
                          </div>
                        </Col> */}
                      </Row>

                      <div className="table-scroll my-3" style={{
                        maxHeight: "500px",
                        overflowY: "auto",
                        overflowX: "auto",
                        borderRadius: "8px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                      }}>
                        <Table striped className="shadow rounded sm my-4 mb-0" style={{
                          marginBottom: 0,
                          borderCollapse: "separate",
                          borderSpacing: 0
                        }}>
                          <TableHeader classes={{ th: 'bg-custom text-white sticky-header' }} />
                          {
                            (items && Array.isArray(items)) ?
                              <TableBody />
                              :
                              <tbody className="">
                                <tr>
                                  <td colSpan={100} align="center" style={{ padding: "2rem" }}>
                                    <div style={{ color: "#6c757d" }}>
                                      <i className="fa fa-inbox fa-2x mb-2"></i>
                                      <p className="mb-0">No items found.</p>
                                    </div>
                                  </td>
                                </tr>
                              </tbody>
                          }
                        </Table>
                      </div>
                      <Pagination
                        classes={{
                          buttonGroup: "btn-custom",
                          button: "bg-custom border-white",
                        }}
                      />
                    </DatatableWrapper>
                  </Tab>

                  {/* Tab 2: Bill of Material (BOM Line Items) */}
                  {/* BOM Line Items – View Only (Quotation Detail) */}
                  <Tab eventKey="bom" title="Bill of Material">
                    <Row className="view-form-header bg-custom align-items-center mt-3">
                      <Col lg={12}>
                        <h5>Bill of Material (BOM Line Items)</h5>
                      </Col>
                    </Row>

                    {bomItems && bomItems.length > 0 ? (
                      <DatatableWrapper
                        body={bomItems}
                        headers={[
                          {
                            title: "Category",
                            prop: "display_category",
                            isFilterable: true,
                            // isSortable: true,
                          },
                          {
                            title: "Item Name",
                            prop: "item_name",
                            isFilterable: true,
                            // isSortable: true,
                          },
                          {
                            title: "Description",
                            prop: "description",
                            isFilterable: true,
                            // isSortable: true,
                          },
                          {
                            title: "Qty",
                            prop: "quantity",
                            // isSortable: true,
                            // isFilterable: true,
                          },
                          {
                            title: "Unit",
                            prop: "unit",
                            isFilterable: true,
                            // isSortable: true,
                          },
                          {
                            title: "Brand",
                            prop: "brand_name",
                            isFilterable: true,
                            // isSortable: true,
                            cell: (row) => {
                              const brands = row.brand_name ? row.brand_name.split(",").map(b => b.trim()) : [];
                              if (brands.length === 0) return "-";
                              return (
                                <div className="d-flex flex-wrap gap-1">
                                  {brands.map((b, i) => {
                                    const label = makeMap[b] || b;
                                    return (
                                      <Badge key={i} bg="primary" className="bg-opacity-10 text-primary border border-primary border-opacity-25 fw-medium" style={{ fontSize: "11px" }}>{label}</Badge>
                                    );
                                  })}
                                </div>
                              );
                            }
                          },
                        ]}
                        paginationOptionsProps={{
                          initialState: {
                            rowsPerPage: 10,
                            options: [5, 10, 15, 20],
                          },
                        }}
                      >
                        <Row className="align-items-end my-2">
                          <Col lg={2} md={4} xl={2} sm={12}>
                            <Filter
                              classes={{
                                inputGroup: "",
                                input: "",
                                clearButton: "btn-custom",
                              }}
                            />
                          </Col>
                          <Col md={4} xl={1} lg={1} sm={12}>
                            <PaginationOptions labels={labels} />
                          </Col>
                          {/* <Col lg={2} md={4} sm={12} className="py-1">
                            <InfoPill
                              left="Total"
                              right={bomItems?.length}
                              rightClasses="bg-custom text-white border-white fw-bold"
                              leftClasses="bg-white fw-bold"
                            />
                          </Col> */}
                        </Row>
                        <div className="table-scroll my-3" style={{
                          maxHeight: "500px",
                          overflowY: "auto",
                          overflowX: "auto",
                          borderRadius: "8px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                        }}>
                          <Table striped className="shadow rounded sm my-4 mb-0" style={{
                            marginBottom: 0,
                            borderCollapse: "separate",
                            borderSpacing: 0
                          }}>
                            <TableHeader classes={{ th: "bg-custom text-white sticky-header" }} />
                            {(bomItems && Array.isArray(bomItems)) ? (
                              <TableBody />
                            ) : (
                              <tbody>
                                <tr>
                                  <td colSpan={100} align="center" style={{ padding: "2rem" }}>
                                    <Spinner />
                                  </td>
                                </tr>
                              </tbody>
                            )}
                          </Table>
                        </div>
                        <Pagination
                          classes={{
                            buttonGroup: "btn-custom",
                            button: "bg-custom border-white",
                          }}
                        />
                      </DatatableWrapper>
                    ) : (
                      <div className="text-center py-5">
                        <i className="fa fa-box fa-3x text-muted mb-3"></i>
                        <p className="text-muted">No Bill of Material items added for this quotation.</p>
                      </div>
                    )}
                  </Tab>

                  {/* Tab 3: Mounting Structure */}
                  {/* Created on 2026-02-07 by Farhan Khan - Mounting Structure tab using MountingStructureForm.jsx fields */}
                  <Tab eventKey="mounting-structure" title="Mounting Structure">
                    <Row className="view-form-header bg-custom align-items-center mt-3">
                      <Col lg={12}>
                        <h5>Mounting Structure</h5>
                      </Col>
                    </Row>

                    {mountingStructureItems && mountingStructureItems.length > 0 ? (
                      <div className="mt-3">
                        {mountingStructureItems.map((lineItem, lineItemIndex) => (
                          <div key={lineItem.id || lineItemIndex} className="card mb-3 shadow-sm">
                            <div className="card-header bg-light">
                              <h6 className="mb-0">Line Item #{lineItemIndex + 1}</h6>
                            </div>
                            <div className="card-body">
                              <Row className="mb-2">
                                <Col lg={3}>
                                  <strong>Roof Type:</strong> {lineItem.roof_type || "-"}
                                </Col>
                                <Col lg={3}>
                                  <strong>Structure Height Front:</strong> {lineItem.structure_height_front || "-"} ft
                                </Col>
                                <Col lg={3}>
                                  <strong>Structure Height Back:</strong> {lineItem.structure_height_back || "-"} ft
                                </Col>
                                <Col lg={3}>
                                  <strong>Item Type:</strong> {lineItem.item_type || "-"}
                                </Col>
                              </Row>

                              {/* Tin Shed Structure */}
                              {lineItem.roof_type === "TINSHED" && (
                                <Row className="mb-2">
                                  <Col lg={6}>
                                    <strong>Tin Shed Structure Type:</strong> {(() => {
                                      const val = lineItem.tin_shed_structure_type;
                                      const arr = Array.isArray(val) ? val : (typeof val === 'string' && val.includes(',') ? val.split(',').map(v => v.trim()) : (val ? [val] : []));

                                      // Fix for Part 2: Object/JSON value handling
                                      const cleanArr = arr.map(v => {
                                        if (typeof v === 'object' && v !== null) {
                                          return Object.values(v).join(", ");
                                        }
                                        return v;
                                      }).filter(v => v);

                                      if (cleanArr.length > 0) {
                                        return (
                                          <div className="d-flex flex-wrap gap-1 mt-1">
                                            {cleanArr.map((t, idx) => (
                                              <Badge key={idx} bg="info" className="bg-opacity-10 text-info border border-info border-opacity-25 fw-medium" style={{ fontSize: "11px" }}>{t}</Badge>
                                            ))}
                                          </div>
                                        );
                                      }
                                      return "-";
                                    })()}
                                  </Col>
                                  <Col lg={6}>
                                    <strong>Tin Shed Mounting Type:</strong> {(() => {
                                      const val = lineItem.tin_shed_mounting_type;
                                      const arr = Array.isArray(val) ? val : (typeof val === 'string' && val.includes(',') ? val.split(',').map(v => v.trim()) : (val ? [val] : []));

                                      // Fix for Part 2: Object/JSON value handling
                                      const cleanArr = arr.map(v => {
                                        if (typeof v === 'object' && v !== null) {
                                          return Object.values(v).join(", ");
                                        }
                                        return v;
                                      }).filter(v => v);

                                      if (cleanArr.length > 0) {
                                        return (
                                          <div className="d-flex flex-wrap gap-1 mt-1">
                                            {cleanArr.map((t, idx) => (
                                              <Badge key={idx} bg="info" className="bg-opacity-10 text-info border border-info border-opacity-25 fw-medium" style={{ fontSize: "11px" }}>{t}</Badge>
                                            ))}
                                          </div>
                                        );
                                      }
                                      return "-";
                                    })()}
                                  </Col>
                                </Row>
                              )}

                              {/* Combo Structure */}
                              {lineItem.roof_type === "COMBO" && (
                                <Row className="mb-2">
                                  <Col lg={4}>
                                    <strong>Panels RCC:</strong> {lineItem.combo_panels_rcc || "-"}
                                  </Col>
                                  <Col lg={4}>
                                    <strong>Panels Ground:</strong> {lineItem.combo_panels_ground || "-"}
                                  </Col>
                                  <Col lg={4}>
                                    <strong>Panels Tin Shed:</strong> {lineItem.combo_panels_tinshed || "-"}
                                  </Col>
                                </Row>
                              )}

                              {/* Special Structure */}
                              {lineItem.special_structure_type && (
                                <Row className="mb-2">
                                  <Col lg={6}>
                                    <strong>Special Structure Type:</strong> {lineItem.special_structure_type || "-"}
                                  </Col>
                                  <Col lg={6}>
                                    <strong>Length:</strong> {lineItem.special_structure_length || "-"}
                                  </Col>
                                  {lineItem.special_structure_specs && (
                                    <Col lg={12}>
                                      <strong>Specs:</strong> {lineItem.special_structure_specs}
                                    </Col>
                                  )}
                                  {lineItem.special_structure_type === "CLEANING SYSTEM" && lineItem.cleaning_system && (
                                    <Col lg={12}>
                                      <strong>Cleaning System:</strong> {lineItem.cleaning_system}
                                    </Col>
                                  )}
                                  {lineItem.special_structure_type === "OTHERS" && lineItem.others_text && (
                                    <Col lg={12}>
                                      <strong>Others:</strong> {lineItem.others_text}
                                    </Col>
                                  )}
                                </Row>
                              )}

                              {/* Parts */}
                              {lineItem.parts && Array.isArray(lineItem.parts) && lineItem.parts.length > 0 && (
                                <div className="mt-3">
                                  <h6>Parts:</h6>
                                  <div className="table-responsive">
                                    <Table striped bordered hover size="sm">
                                      <thead className="bg-custom text-white">
                                        <tr>
                                          <th>Part Name</th>
                                          <th>Specs</th>
                                          <th>Size</th>
                                          <th>Shape</th>
                                          <th>Make</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {lineItem.parts.map((part, partIndex) => (
                                          <tr key={`part-${lineItemIndex}-${partIndex}`}>
                                            <td>{part.component_name || "-"}</td>
                                            <td>{part.specs || "-"}</td>
                                            <td>{part.size || "-"}</td>
                                            <td>
                                              {Array.isArray(part.shape)
                                                ? part.shape.map((s, si) => (
                                                  <Badge key={si} bg="info" className="me-1 bg-opacity-10 text-info border border-info border-opacity-25 fw-medium" style={{ fontSize: "11px" }}>{s}</Badge>
                                                ))
                                                : (part.shape ? <Badge bg="info" className="bg-opacity-10 text-info border border-info border-opacity-25 fw-medium" style={{ fontSize: "11px" }}>{part.shape}</Badge> : "-")}
                                            </td>
                                            <td>
                                              {Array.isArray(part.make)
                                                ? part.make.map((m, mi) => {
                                                  const label = makeMap[m] || m;
                                                  return (
                                                    <Badge key={mi} bg="primary" className="me-1 bg-opacity-10 text-primary border border-primary border-opacity-25 fw-medium" style={{ fontSize: "11px" }}>{label}</Badge>
                                                  );
                                                })
                                                : (part.make ? <Badge bg="primary" className="bg-opacity-10 text-primary border border-primary border-opacity-25 fw-medium" style={{ fontSize: "11px" }}>{makeMap[part.make] || part.make}</Badge> : "-")}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <i className="fa fa-box fa-3x text-muted mb-3"></i>
                        <p className="text-muted">No Mounting Structure items added for this quotation.</p>
                      </div>
                    )}
                  </Tab>
                </Tabs>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </Main>
  );
};

export default QuotationView;

